"use client";

import React, { useState } from "react";
import { StandardModal, Button, Input, Label, LoadingSpinner } from "@west-tennessee-consulting/ui";
import { useCreateGoogleDriveFolder } from "@/lib/hooks/google-drive/use-google-drive-browser";
import { toast } from "sonner";
import { FolderPlus } from "lucide-react";

export interface GoogleDriveCreateFolderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId?: string;
  driveId?: string;
  isSecondAccount?: boolean;
  onSuccess?: (folder: any) => void;
}

export function GoogleDriveCreateFolderModal({
  open,
  onOpenChange,
  parentId,
  driveId,
  isSecondAccount = false,
  onSuccess,
}: GoogleDriveCreateFolderModalProps) {
  const [folderName, setFolderName] = useState("");
  const createFolderMutation = useCreateGoogleDriveFolder();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!folderName.trim()) {
      toast.error("Please enter a folder name");
      return;
    }

    console.log("Creating folder with context:", {
      folderName: folderName.trim(),
      parentId,
      driveId,
      isSecondAccount,
    });

    try {
      const result = await createFolderMutation.mutateAsync({
        folderName: folderName.trim(),
        parentId,
        driveId,
        isSecondAccount,
      });

      toast.success(`Folder "${folderName.trim()}" created successfully`);
      setFolderName("");
      onOpenChange(false);
      onSuccess?.(result.folder);
    } catch (error: any) {
      console.error("Error creating folder:", error);
      toast.error(`Failed to create folder: ${error.message}`);
    }
  };

  const handleClose = () => {
    setFolderName("");
    onOpenChange(false);
  };

  return (
    <StandardModal
      isOpen={open}
      onClose={handleClose}
      title="Create New Folder"
      subtitle="Enter a name for the new folder"
      icon={FolderPlus}
      maxWidth="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="folderName">Folder Name</Label>
          <Input
            id="folderName"
            type="text"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            placeholder="Enter folder name..."
            autoFocus
            maxLength={255}
            disabled={createFolderMutation.isPending}
          />
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={createFolderMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createFolderMutation.isPending || !folderName.trim()}>
            {createFolderMutation.isPending ? (
              <>
                <LoadingSpinner className="h-4 w-4 mr-2" />
                Creating...
              </>
            ) : (
              <>
                <FolderPlus className="h-4 w-4 mr-2" />
                Create Folder
              </>
            )}
          </Button>
        </div>
      </form>
    </StandardModal>
  );
}
